import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiArrowRight, FiChevronDown, FiBookOpen,
  FiClock, FiTag, FiHome, FiUser,
  FiMapPin, FiHeart, FiStar, FiLock,
} from 'react-icons/fi';
import DateTimePicker from '../components/common/DateTimePicker';
import './PSEOPage.css';

const setMeta = (name, content, isProperty = false) => {
  if (typeof document === 'undefined') return;
  const attr = isProperty ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (data) => {
  if (typeof document === 'undefined') return;
  document.head.querySelectorAll('script[data-pseo-jsonld]').forEach((s) => s.remove());
  const list = Array.isArray(data) ? data : [data];
  list.forEach((d, i) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-pseo-jsonld', String(i));
    s.text = JSON.stringify(d);
    document.head.appendChild(s);
  });
};

const PSEOPage = ({ prefetched = null }) => {
  const { slug } = useParams();
  const [page, setPage] = useState(prefetched);
  const [loading, setLoading] = useState(!prefetched);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    // If we were handed a prefetched page for this slug, skip the network round-trip.
    if (prefetched && prefetched.slug === slug) {
      setPage(prefetched);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setPage(null);

    fetch(`/api/pages/${slug}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (!j.success) throw new Error(j.error || 'Page not found');
        setPage(j.data);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, [slug, prefetched]);

  useEffect(() => {
    if (!page) return;

    document.title = page.title;
    setMeta('description', page.metaDescription);
    setMeta(
      'keywords',
      page.metaKeywords ||
        [page.h1, ...(page.secondaryKeywords || []), page.categoryLabel, 'AstroePanchang'].join(', ')
    );

    setMeta('og:title', page.title, true);
    setMeta('og:description', page.metaDescription, true);
    setMeta('og:url', page.canonicalUrl, true);
    setMeta('og:type', 'article', true);
    setMeta('og:image', `https://www.astroepanchang.com${page.image}`, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', page.title);
    setMeta('twitter:description', page.metaDescription);

    setCanonical(page.canonicalUrl);
    setJsonLd(page.jsonLd);

    return () => {
      document.querySelectorAll('script[data-pseo-jsonld]').forEach((s) => s.remove());
    };
  }, [page]);

  if (loading) {
    return (
      <div className="pseo-loading">
        <div className="pseo-spinner" />
        <p>Loading page…</p>
      </div>
    );
  }

  // Derive presentation intent: prefer the server-stored value, fall back to
  // keyword detection so older pages still render the right form + glyph.
  const intent = page ? (page.intent || deriveIntent(page.h1, page.category)) : null;

  if (error || !page) {
    return (
      <div className="pseo-error">
        <h1>Page not found</h1>
        <p>{error || `No page exists at /blog/${slug}.`}</p>
        <Link to="/" className="pseo-cta-btn">Back to Home</Link>
      </div>
    );
  }

  return (
    <article className="pseo">
      {/* Breadcrumbs */}
      <nav className="pseo-crumbs" aria-label="Breadcrumb">
        <ol>
          <li><Link to="/"><FiHome /> Home</Link></li>
          <li><span aria-hidden>›</span></li>
          <li><span>{page.categoryLabel}</span></li>
          <li><span aria-hidden>›</span></li>
          <li aria-current="page"><span>{page.h1}</span></li>
        </ol>
      </nav>

      {/* Hero */}
      <header className="pseo-hero">
        <div className="pseo-hero-meta">
          <span className="pseo-tag"><FiTag /> {page.categoryLabel}</span>
          <span className="pseo-meta-item"><FiClock /> Updated {new Date(page.updatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          <span className="pseo-meta-item"><FiBookOpen /> {Math.ceil(page.wordCount / 220)} min read</span>
        </div>

        <motion.h1
          className="pseo-h1"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {page.h1}
        </motion.h1>

        <p className="pseo-intro">{page.intro}</p>
      </header>

      {/* Hero illustration — intent-driven, slug-seeded variation */}
      <div
        className={`pseo-image pseo-variant-${slugVariant(page.slug, 4)}`}
        role="img"
        aria-label={page.h1}
      >
        <HeroArt intent={intent} slug={page.slug} />
        <div className="pseo-image-overlay">
          <span>{page.categoryLabel}</span>
          <strong>{page.h1}</strong>
        </div>
      </div>

      {/* Sections */}
      <section className="pseo-content">
        {page.sections.map((s, i) => (
          <motion.div
            key={i}
            className="pseo-section"
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
          >
            <h2>{s.heading}</h2>
            <p>{s.body}</p>
          </motion.div>
        ))}
      </section>

      {/* Intent-driven action form */}
      <CategoryForm category={intent} h1={page.h1} />

      {/* FAQ */}
      <section className="pseo-faq">
        <div className="pseo-section-head">
          <h2>Frequently Asked Questions</h2>
          <p>The most common questions about {page.h1}, answered by AstroePanchang's Vedic astrologers.</p>
        </div>
        <div className="pseo-faq-list">
          {page.faqs.map((f, i) => (
            <div
              key={i}
              className={`pseo-faq-item ${openFaq === i ? 'open' : ''}`}
              onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
            >
              <div className="pseo-faq-q">
                <span>{f.q}</span>
                <FiChevronDown />
              </div>
              {openFaq === i && <div className="pseo-faq-a">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* People also search for — secondary keyword chips */}
      {page.secondaryKeywords && page.secondaryKeywords.length > 0 && (
        <section className="pseo-keywords">
          <h3>People also search for</h3>
          <div className="pseo-keyword-chips">
            {page.secondaryKeywords.map((kw, i) => (
              <Link
                key={i}
                to={`/blog?q=${encodeURIComponent(kw)}`}
                className="pseo-kw-chip"
              >
                {kw}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      <section className="pseo-related">
        <div className="pseo-related-col">
          <h3>Related Services</h3>
          <ul>
            {page.relatedServices.map((s, i) => (
              <li key={i}>
                <Link to={s.path}>{s.label} <FiArrowRight /></Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="pseo-related-col">
          <h3>Recommended Reading</h3>
          <ul>
            {page.relatedBlogs.map((b, i) => (
              <li key={i}>
                <Link to={`/blog/${b.slug}`}>{b.label} <FiArrowRight /></Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="pseo-cta">
        <div className="pseo-cta-inner">
          <h2>Ready for a personalised {page.h1} reading?</h2>
          <p>Get a fully personalised analysis from AstroePanchang's qualified Vedic astrologers — sidereal Lahiri calculations, custom remedies, and a written report you can keep.</p>
          <Link to={page.cta.path} className="pseo-cta-btn">
            {page.cta.label} <FiArrowRight />
          </Link>
        </div>
      </section>
    </article>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Intent detection — mirrors backend so legacy pages still pick correctly  */
/* ──────────────────────────────────────────────────────────────────────── */

const INTENT_KEYWORDS = [
  { intent: 'matchmaking', keywords: ['match', 'porutham', 'marriage', 'compatibility', 'bride', 'groom', 'kundli matching', 'jathagam porutham', 'manglik', 'mangal dosh', 'nadi dosha', 'guna milan', 'horoscope matching'] },
  { intent: 'horoscope',   keywords: ['horoscope', 'kundli', 'birth chart', 'rashi', 'jathagam', 'natal', 'date of birth', 'sun sign', 'moon sign', 'lagna', 'ascendant', 'dasha', 'zodiac', 'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces', 'mesham', 'rishabam', 'kataka'] },
  { intent: 'panchangam',  keywords: ['panchangam', 'panchang', 'rahu kalam', 'rahukalam', 'muhurtham', 'muhurat', 'tithi', 'nakshatra', 'yoga', 'karana', 'auspicious time', 'gulika', 'yamaganda', 'abhijit', 'sunrise', 'sunset', 'today', 'tomorrow'] },
];

const deriveIntent = (topic, category) => {
  const t = String(topic || '').toLowerCase();
  for (const { intent, keywords } of INTENT_KEYWORDS) {
    if (keywords.some((k) => t.includes(k))) return intent;
  }
  return category;
};

// Deterministic per-slug pseudo-random — so each page renders the same art
// every time it loads, but different pages get different variants.
const slugVariant = (slug, mod) => {
  let h = 0;
  const s = String(slug || '');
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % mod;
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Hero illustration — intent glyph + slug-seeded star pattern variant      */
/* ──────────────────────────────────────────────────────────────────────── */

const ZODIAC_GLYPHS = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];
const INTENT_GLYPH = {
  panchangam:  '☽',
  horoscope:   '☉',
  matchmaking: '∞',
  astrology:   '✦',
};

const STAR_VARIANTS = [
  {
    stars: [[110, 70, 1.5], [220, 130, 1], [180, 230, 1.2], [80, 280, 1.4], [340, 90, 1], [400, 240, 1.3], [260, 60, 0.8], [50, 180, 1], [430, 130, 1.1], [150, 340, 1], [310, 320, 1.3], [40, 60, 1]],
    lines: ['M 110 70 L 220 130 L 180 230 L 80 280', 'M 340 90 L 400 240 L 310 320'],
  },
  {
    stars: [[60, 120, 1.3], [180, 60, 1], [320, 170, 1.4], [240, 280, 1.2], [100, 320, 1.1], [400, 80, 1.5], [380, 350, 1], [40, 240, 1.2], [200, 220, 1], [260, 50, 1.3], [450, 200, 1.1], [120, 200, 0.9]],
    lines: ['M 60 120 L 180 60 L 320 170 L 240 280', 'M 400 80 L 380 350 L 100 320'],
  },
  {
    stars: [[140, 60, 1.4], [260, 130, 1.1], [80, 200, 1.3], [200, 280, 1.5], [340, 280, 1.2], [400, 120, 1], [430, 320, 1.4], [50, 320, 1.1], [180, 180, 1], [310, 70, 1.2], [380, 220, 1.3], [110, 280, 0.9]],
    lines: ['M 140 60 L 260 130 L 80 200 L 200 280', 'M 340 280 L 400 120 L 430 320'],
  },
  {
    stars: [[90, 90, 1.2], [200, 50, 1.3], [310, 110, 1], [380, 200, 1.5], [430, 290, 1.1], [310, 250, 1], [200, 320, 1.4], [80, 240, 1.2], [40, 140, 1.3], [180, 170, 0.9], [260, 200, 1], [340, 60, 1.2]],
    lines: ['M 90 90 L 200 50 L 310 110 L 380 200', 'M 430 290 L 200 320 L 40 140'],
  },
];

const HeroArt = ({ intent, slug = '' }) => {
  const glyph = INTENT_GLYPH[intent] || '✦';
  const variant = STAR_VARIANTS[slugVariant(slug + '_s', STAR_VARIANTS.length)];

  const spokes = Array.from({ length: 12 }, (_, i) => {
    const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
    return {
      x1: Math.cos(a) * 95,  y1: Math.sin(a) * 95,
      x2: Math.cos(a) * 158, y2: Math.sin(a) * 158,
      gx: Math.cos(a) * 125, gy: Math.sin(a) * 125 + 5,
      glyph: ZODIAC_GLYPHS[i],
    };
  });

  // Tilt the wheel slightly per page for added variety (0°, 7.5°, 15°, 22.5°)
  const wheelRotation = slugVariant(slug + '_r', 4) * 7.5;

  return (
    <svg
      className="pseo-hero-svg"
      viewBox="0 0 1200 400"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="pseoCenterGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="rgba(255,255,255,0.55)" />
          <stop offset="60%" stopColor="rgba(255,255,255,0.12)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <linearGradient id="pseoStrokeGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="rgba(255,255,255,0.85)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.45)" />
        </linearGradient>
      </defs>

      {/* Star field — varies per slug */}
      <g opacity="0.65">
        {variant.stars.map(([cx, cy, r], i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="white" />
        ))}
      </g>

      {/* Constellation lines — varies per slug */}
      <g stroke="rgba(255,255,255,0.25)" strokeWidth="0.6" fill="none">
        {variant.lines.map((d, i) => <path key={i} d={d} />)}
      </g>

      {/* Zodiac wheel — same structure, slight rotation per slug */}
      <g transform={`translate(960, 200) rotate(${wheelRotation})`}>
        <circle cx="0" cy="0" r="170" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.8" strokeDasharray="2 5" />

        {Array.from({ length: 27 }).map((_, i) => {
          const a = (i / 27) * Math.PI * 2 - Math.PI / 2;
          return (
            <line key={`nak-${i}`}
              x1={Math.cos(a) * 158} y1={Math.sin(a) * 158}
              x2={Math.cos(a) * 168} y2={Math.sin(a) * 168}
              stroke="rgba(255,255,255,0.7)" strokeWidth="0.7"
            />
          );
        })}

        <circle cx="0" cy="0" r="158" fill="none" stroke="url(#pseoStrokeGrad)" strokeWidth="1.2" />

        {spokes.map((s, i) => (
          <line key={`spoke-${i}`}
            x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
            stroke="rgba(255,255,255,0.55)" strokeWidth="0.8"
          />
        ))}

        <circle cx="0" cy="0" r="95" fill="none" stroke="url(#pseoStrokeGrad)" strokeWidth="1.6" />

        {/* Counter-rotate the glyphs so they stay upright regardless of wheel tilt */}
        <g transform={`rotate(${-wheelRotation})`}>
          {spokes.map((s, i) => (
            <text key={`g-${i}`}
              x={s.gx} y={s.gy}
              textAnchor="middle" fontSize="16" fill="white" opacity="0.85"
              style={{ fontFamily: 'serif' }}
            >
              {s.glyph}
            </text>
          ))}
        </g>

        <circle cx="0" cy="0" r="80" fill="url(#pseoCenterGlow)" />

        <g transform={`rotate(${-wheelRotation})`}>
          <text x="0" y="22"
            textAnchor="middle" fontSize="64" fill="white" opacity="0.95"
            style={{ fontFamily: 'serif', fontWeight: 600 }}
          >
            {glyph}
          </text>
        </g>
      </g>
    </svg>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Category-specific lead-in form                                            */
/* ──────────────────────────────────────────────────────────────────────── */

const CategoryForm = ({ category, h1 }) => {
  if (category === 'matchmaking') return <MatchmakingForm topic={h1} />;
  if (category === 'panchangam')  return <PanchangamForm topic={h1} />;
  return <BirthDetailsForm category={category} topic={h1} />;
};

/* City autocomplete — hits /api/panchang/cities, debounced, with selectable suggestions.
   When a city is picked, full {city, state, country, latitude, longitude, timezone}
   is bubbled up via onSelect so downstream pages can skip the geocoding round-trip. */
const CityAutocomplete = ({ value, onChange, onSelect, placeholder = 'Search city...' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [locked, setLocked] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (locked) return;
    if (!value || value.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/panchang/cities?q=${encodeURIComponent(value)}`);
        const json = await res.json();
        setSuggestions(json.data || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value, locked]);

  const handleSelect = (city) => {
    const display = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    onChange(display);
    onSelect(city);
    setLocked(true);
    setOpen(false);
  };

  const handleType = (e) => {
    onChange(e.target.value);
    onSelect(null);
    setLocked(false);
  };

  return (
    <div className="pseo-city-search" ref={ref}>
      <input
        type="text"
        value={value}
        onChange={handleType}
        placeholder={placeholder}
        autoComplete="off"
        required
      />
      {open && suggestions.length > 0 && (
        <ul className="pseo-city-list">
          {suggestions.map((c, i) => (
            <li key={i} onMouseDown={() => handleSelect(c)}>
              <FiMapPin />
              <div>
                <strong>{c.city}</strong>
                <span>{c.state ? `${c.state}, ` : ''}{c.country}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BirthDetailsForm = ({ category, topic }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [place, setPlace] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isHoroscope = category === 'horoscope';

  const submit = (e) => {
    e.preventDefault();
    setError('');
    if (!name || !date || !time || !place) {
      return setError('Please fill all fields.');
    }
    setSubmitting(true);
    // Same payload shape Kundli.jsx already consumes (formData + selectedLocation).
    // Kundli auto-generates on mount when both date and time are present.
    navigate('/online-horoscope-generator', {
      state: {
        formData: { name, gender, date, time, place },
        selectedLocation,
        fromTopic: topic,
      },
    });
  };

  return (
    <section className="pseo-form-section">
      <form className="pseo-form-card" onSubmit={submit}>
        <header className="pseo-form-head">
          <h2 className="pseo-form-title">
            {isHoroscope ? 'Get Your Free Horoscope' : 'Generate Your Free Kundli'}
          </h2>
          <p>Enter your birth details for an instant Vedic analysis tailored to <strong>{topic}</strong>.</p>
        </header>

        <div className="pseo-form-row">
          <label className="pseo-form-field">
            <span className="pseo-form-label"><FiUser /> Full Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </label>
        </div>

        <div className="pseo-form-row pseo-form-grid-2">
          <label className="pseo-form-field">
            <span className="pseo-form-label">Gender</span>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="pseo-form-select"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </label>

          <label className="pseo-form-field">
            <span className="pseo-form-label"><FiMapPin /> Birth Place</span>
            <CityAutocomplete
              value={place}
              onChange={setPlace}
              onSelect={setSelectedLocation}
              placeholder="Search city..."
            />
          </label>
        </div>

        <div className="pseo-form-row pseo-dtp">
          <DateTimePicker
            date={date}
            time={time}
            onDateChange={setDate}
            onTimeChange={setTime}
            dateLabel="Date of Birth"
            timeLabel="Time of Birth"
          />
        </div>

        {error && (
          <div className="pseo-form-error">{error}</div>
        )}

        <button type="submit" className="pseo-form-btn" disabled={submitting}>
          {isHoroscope ? 'Generate My Horoscope' : 'Generate My Kundli'}
          <FiArrowRight />
        </button>

        <p className="pseo-form-hint">
          <FiLock /> Your data is secure and private. Used only for this calculation.
        </p>
      </form>
    </section>
  );
};

const MatchmakingForm = ({ topic }) => {
  const navigate = useNavigate();
  const [bride, setBride] = useState({ name: '', date: '', time: '', place: '' });
  const [groom, setGroom] = useState({ name: '', date: '', time: '', place: '' });
  const [brideLocation, setBrideLocation] = useState(null);
  const [groomLocation, setGroomLocation] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateBride = (k) => (v) => setBride((p) => ({ ...p, [k]: v }));
  const updateGroom = (k) => (v) => setGroom((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError('');
    const missing = [];
    if (!bride.name || !bride.date || !bride.time || !bride.place) missing.push('bride');
    if (!groom.name || !groom.date || !groom.time || !groom.place) missing.push('groom');
    if (missing.length) {
      return setError(`Please complete the ${missing.join(' and ')} details.`);
    }
    setSubmitting(true);
    // MatchMaking.jsx will read this state, pre-fill its form,
    // and auto-trigger /api/match-making/compatibility on mount.
    navigate('/match-making', {
      state: {
        brideData: bride,
        groomData: groom,
        brideLocation,
        groomLocation,
        fromTopic: topic,
      },
    });
  };

  return (
    <section className="pseo-form-section">
      <form className="pseo-form-card pseo-form-wide" onSubmit={submit}>
        <header className="pseo-form-head">
          <h2 className="pseo-form-title">Check Your Match Compatibility</h2>
          <p>Compare 12 poruthams + manglik dosha for the bride and groom — instant Vedic analysis aligned with <strong>{topic}</strong>.</p>
        </header>

        <div className="pseo-match-grid">
          <div className="pseo-match-col">
            <div className="pseo-match-head pseo-match-bride">
              <FiHeart /> Bride Details
            </div>
            <label className="pseo-form-field">
              <span className="pseo-form-label"><FiUser /> Full Name</span>
              <input
                type="text"
                value={bride.name}
                onChange={(e) => updateBride('name')(e.target.value)}
                placeholder="Bride's full name"
                required
              />
            </label>
            <div className="pseo-dtp">
              <DateTimePicker
                date={bride.date}
                time={bride.time}
                onDateChange={updateBride('date')}
                onTimeChange={updateBride('time')}
              />
            </div>
            <label className="pseo-form-field">
              <span className="pseo-form-label"><FiMapPin /> Place of Birth</span>
              <CityAutocomplete
                value={bride.place}
                onChange={updateBride('place')}
                onSelect={setBrideLocation}
              />
            </label>
          </div>

          <div className="pseo-match-divider"><FiHeart /></div>

          <div className="pseo-match-col">
            <div className="pseo-match-head pseo-match-groom">
              <FiHeart /> Groom Details
            </div>
            <label className="pseo-form-field">
              <span className="pseo-form-label"><FiUser /> Full Name</span>
              <input
                type="text"
                value={groom.name}
                onChange={(e) => updateGroom('name')(e.target.value)}
                placeholder="Groom's full name"
                required
              />
            </label>
            <div className="pseo-dtp">
              <DateTimePicker
                date={groom.date}
                time={groom.time}
                onDateChange={updateGroom('date')}
                onTimeChange={updateGroom('time')}
              />
            </div>
            <label className="pseo-form-field">
              <span className="pseo-form-label"><FiMapPin /> Place of Birth</span>
              <CityAutocomplete
                value={groom.place}
                onChange={updateGroom('place')}
                onSelect={setGroomLocation}
              />
            </label>
          </div>
        </div>

        {error && (
          <div className="pseo-form-error">{error}</div>
        )}

        <button type="submit" className="pseo-form-btn" disabled={submitting}>
          <FiHeart /> Check Compatibility
          <FiArrowRight />
        </button>

        <p className="pseo-form-hint">
          <FiLock /> Computes 12-porutham + manglik dosha for both partners. Your data is private.
        </p>
      </form>
    </section>
  );
};

const PanchangamForm = ({ topic }) => {
  const navigate = useNavigate();
  const [city, setCity] = useState('');
  const [, setCityLocation] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const submit = (e) => {
    e.preventDefault();
    navigate('/panchangam', { state: { city, date, fromTopic: topic } });
  };

  return (
    <section className="pseo-form-section">
      <form className="pseo-form-card" onSubmit={submit}>
        <header className="pseo-form-head">
          <h2 className="pseo-form-title">View Live Panchangam</h2>
          <p>Get tithi, nakshatra, yoga, karana and rahu kalam for your city — accurate to the second using Lahiri ayanamsa.</p>
        </header>

        <div className="pseo-form-row pseo-form-grid-2">
          <label className="pseo-form-field">
            <span className="pseo-form-label"><FiMapPin /> City</span>
            <CityAutocomplete
              value={city}
              onChange={setCity}
              onSelect={setCityLocation}
              placeholder="Search city..."
            />
          </label>

          <label className="pseo-form-field">
            <span className="pseo-form-label">Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </label>
        </div>

        <button type="submit" className="pseo-form-btn">
          <FiStar /> View Panchangam
          <FiArrowRight />
        </button>

        <p className="pseo-form-hint">
          <FiLock /> Real-time calculation from VSOP87 ephemeris + Lahiri ayanamsa.
        </p>
      </form>
    </section>
  );
};

export default PSEOPage;
